import { comeData, giornoMese, oggiIso, type Prenotazione } from "@/features/ritiri/api";
import type { RitiriOggi } from "../data/mockRitiri";

interface OrdiniRitiriSideProps {
  ritiri: RitiriOggi;
  /** Il prossimo ritiro SDA prenotato: undefined = in lettura, null = nessuno,
   *  "errore" = non letto (mai scriverlo come «nessuno»). */
  prossimo?: Prenotazione | null | "errore";
}

/** «oggi» o «gio 02/10». */
function giornoBreve(iso: string): string {
  if (iso === oggiIso()) return "oggi";
  const g = comeData(iso).toLocaleDateString("it-IT", { weekday: "short" }).replace(".", "");
  return `${g} ${giornoMese(iso)}`;
}

/** Helper formattazione numeri italiani: 1.665 (3 decimali) per il volume,
 *  449.20 (2 decimali) per il peso. Replica esatta del legacy
 *  ``_aggiorna_riepilogo_ritiri`` in tab_ordini.py. */
function fmt(n: number, decimals: number): string {
  return n.toLocaleString("it-IT", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Pannello "Ritiri di oggi": aggrega per carrier gli ordini con mail
 * "merce pronta" ricevuta oggi. Replica del pannello legacy
 * (Poleepo Manager App). SDA mostra quando passa il prossimo ritiro prenotato.
 * BRT mostra split colli pacchi vs colli bancali (per dimensionare
 * correttamente il ritiro).
 *
 * Dal 2026-09-23 e' la ZONA DESTRA della plancia unica della lista ordini
 * (Davide: «incorpora filtri, tasti azione e ritiri in una sola scheda»): la
 * cornice la da' la plancia, qui niente bordi propri e niente box dentro box —
 * SDA e BRT sono due colonne divise da un filo. Dal 30/09 la zona ha misura
 * FISSA anche senza ritiri (Davide: non deve cambiare dimensione).
 */
export function OrdiniRitiriSide({ ritiri, prossimo }: OrdiniRitiriSideProps) {
  const sda = ritiri.sda;
  const brt = ritiri.brt;
  const brtTotalColli = brt.colli_pacchi + brt.colli_bancali;
  const brtTotalOrders = brt.orders_pacchi + brt.orders_bancali;
  const sdaEmpty = sda.colli === 0 && sda.orders === 0;
  const brtEmpty = brtTotalOrders === 0;
  const righeSda: Riga[] = [
    ["Volume", `${fmt(sda.volume_m3, 3)} m³`],
    ["Peso", `${fmt(sda.peso_kg, 2)} kg`],
    // Davide 30/09: al posto delle misure del collo, quando passa il corriere.
    [
      "Prossimo",
      prossimo === undefined
        ? "…"
        : prossimo === "errore"
          ? "n.d."
          : prossimo === null
            ? "nessuno"
            : giornoBreve(prossimo.data_ritiro),
    ],
  ];
  // Split pacchi/bancali solo dove c'e': colli (ordini).
  const righeBrt: Riga[] = [
    ...(brt.orders_pacchi > 0
      ? [["Pacchi", `${brt.colli_pacchi} (${brt.orders_pacchi})`] as const]
      : []),
    ...(brt.orders_bancali > 0
      ? [["Bancali", `${brt.colli_bancali} (${brt.orders_bancali})`] as const]
      : []),
    ["Volume", `${fmt(brt.volume_m3, 3)} m³`],
    ["Peso", `${fmt(brt.peso_kg, 2)} kg`],
  ];

  return (
    <div className="flex h-full w-[300px] flex-col gap-2 px-4 py-3 min-[1400px]:w-[340px]">
      <div className="flex items-baseline justify-between">
        <span className="font-display text-[14px] font-semibold text-ink">Ritiri di oggi</span>
        <span className="text-[12px] font-semibold tabular-nums text-ink">
          {ritiri.date_display}
        </span>
      </div>

      <div className="grid flex-1 grid-cols-2 divide-x divide-line">
        <CarrierColumn
          testId="ritiri-sda"
          carrier="SDA"
          empty={sdaEmpty}
          totalColli={sda.colli}
          totalOrders={sda.orders}
          righe={righeSda}
          titolo={
            prossimo === "errore"
              ? "Prossimo ritiro non leggibile: apri la pagina Ritiri"
              : prossimo
                ? `Prossimo ritiro: ${prossimo.magazzino ?? ""} · ${prossimo.colli} colli${prossimo.codice ? ` · ${prossimo.codice}` : ""}`
                : undefined
          }
        />
        <CarrierColumn
          testId="ritiri-brt"
          carrier="BRT"
          empty={brtEmpty}
          totalColli={brtTotalColli}
          totalOrders={brtTotalOrders}
          righe={righeBrt}
        />
      </div>

      {/* OTHER (corrieri secondari): visibile solo se ci sono ordini, su
          UNA riga per non aggiungere altezza. Senza split, solo il totale. */}
      {ritiri.other.orders > 0 && (
        <div className="flex items-center justify-between border-t border-line pt-1 text-[12px]">
          <span className="text-ink">Altri corrieri</span>
          <span className="font-semibold tabular-nums text-ink">
            {ritiri.other.colli} colli · {ritiri.other.orders} ord.
          </span>
        </div>
      )}
    </div>
  );
}

/** Etichetta → valore, nell'ordine in cui si leggono. */
type Riga = readonly [string, string];

interface CarrierColumnProps {
  testId: string;
  carrier: string;
  empty: boolean;
  totalColli: number;
  totalOrders: number;
  righe: Riga[];
  titolo?: string;
}

/** Una colonna per corriere: stesso scheletro piena o vuota, cosi' SDA e BRT
 *  restano allineate. In testa il numero che conta (i colli), sotto le misure
 *  etichetta a sinistra, valore a destra. */
function CarrierColumn({
  testId,
  carrier,
  empty,
  totalColli,
  totalOrders,
  righe,
  titolo,
}: CarrierColumnProps) {
  return (
    <div
      data-testid={testId}
      className="flex min-w-0 flex-col gap-[3px] px-2.5 first:pl-0 last:pr-0"
    >
      <div className="flex items-baseline justify-between gap-1">
        <span className="text-[12px] font-bold tracking-[0.04em] text-ink">{carrier}</span>
        {!empty && (
          <span className="flex items-baseline gap-1 whitespace-nowrap">
            <span className="font-display text-[19px] font-semibold leading-none tabular-nums text-ink">
              {totalColli}
            </span>
            <span className="text-[11.5px] text-ink">colli · {totalOrders} ord.</span>
          </span>
        )}
      </div>
      {empty && <div className="text-[12px] font-medium text-ink">Nessun ritiro oggi</div>}
      {/* Vuota o piena, le righe fisse restano (il «Prossimo» SDA vale anche
          senza merce pronta oggi): la zona non cambia misura. */}
      <dl className="flex flex-col gap-0.5 text-[12px]" title={titolo}>
        {righe
          .filter(([etichetta]) => !empty || etichetta === "Prossimo")
          .map(([etichetta, valore]) => (
            <div key={etichetta} className="flex justify-between gap-2">
              <dt className="text-ink">{etichetta}</dt>
              <dd className="whitespace-nowrap font-semibold tabular-nums text-ink">{valore}</dd>
            </div>
          ))}
      </dl>
    </div>
  );
}
